import React, { useEffect, useState } from 'react';
import { SecurityUtils } from '../utils/security';
import { config } from '../config/appConfig';

interface SecurityProviderProps {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize security features
    const initializeSecurity = () => {
      // Generate and set CSRF token if enabled
      if (config.security.enableCsrfProtection) {
        const csrfToken = SecurityUtils.generateCSRFToken();
        SecurityUtils.setCSRFToken(csrfToken);
        SecurityUtils.setSecureCookie('csrf-token', csrfToken);
      }

      // Remove any existing CSP meta tags first
      const existingCSP = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
      console.log('Found existing CSP tags:', existingCSP.length);
      existingCSP.forEach(meta => {
        console.log('Removing CSP:', meta.getAttribute('content'));
        meta.remove();
      });

      // For development, add a very permissive CSP that allows all necessary connections
      if (config.environment === 'development') {
        const cspContent = [
          "default-src 'self'",
          "script-src 'self' 'unsafe-inline' 'unsafe-eval' https: http:",
          "style-src 'self' 'unsafe-inline' https: http:",
          "img-src 'self' data: https: http: blob:",
          "font-src 'self' https: http: data:",
          "connect-src 'self' https: http: ws: wss:",
          "frame-src 'self' https: http:",
          "object-src 'none'",
          "base-uri 'self'",
          "form-action 'self'"
        ].join('; ');

        // Remove all existing CSP tags first
        const allCSPTags = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
        allCSPTags.forEach(tag => tag.remove());
        
        // Add new permissive CSP
        const cspMeta = document.createElement('meta');
        cspMeta.httpEquiv = 'Content-Security-Policy';
        cspMeta.content = cspContent;
        document.head.insertBefore(cspMeta, document.head.firstChild);
        
        console.log('Added permissive CSP for development:', cspContent);
      } else {
        console.log('No CSP added for production - Firebase should work now');
      }

      // Set additional security headers
      const securityMeta = [
        { name: 'X-Content-Type-Options', content: 'nosniff' },
        { name: 'X-Frame-Options', content: 'DENY' },
        { name: 'X-XSS-Protection', content: '1; mode=block' },
        { name: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' }
      ];

      securityMeta.forEach(meta => {
        const metaElement = document.createElement('meta');
        metaElement.httpEquiv = meta.name;
        metaElement.content = meta.content;
        document.head.appendChild(metaElement);
      });

      setIsInitialized(true);
    };

    initializeSecurity();
  }, []);

  // Session timeout management
  useEffect(() => {
    if (!config.security.sessionTimeout) return;

    let timeoutId: NodeJS.Timeout;

    const resetTimeout = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Handle session timeout
        console.warn('Session timeout reached');
        // You can implement logout logic here
      }, config.security.sessionTimeout);
    };

    // Reset timeout on user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimeout, true);
    });

    resetTimeout();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => {
        document.removeEventListener(event, resetTimeout, true);
      });
    };
  }, []);

  if (!isInitialized) {
    return <div>Loading security features...</div>;
  }

  return <>{children}</>;
};

// Security HOC for protected routes
export const withSecurity = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const [isSecure, setIsSecure] = useState(false);

    useEffect(() => {
      // Check if running in secure context
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:';
      
      if (!isSecureContext && config.environment === 'production') {
        console.warn('Application is not running in a secure context');
      }

      setIsSecure(true);
    }, []);

    if (!isSecure) {
      return <div>Initializing security...</div>;
    }

    return <Component {...props} />;
  };
};

// Rate limiting hook
export const useRateLimit = (key: string, maxRequests: number = 10, windowMs: number = 60000) => {
  const [isAllowed, setIsAllowed] = useState(true);
  const [remainingRequests, setRemainingRequests] = useState(maxRequests);

  const checkLimit = () => {
    const allowed = SecurityUtils.checkRateLimit(key, maxRequests, windowMs);
    setIsAllowed(allowed);
    
    // This is a simplified implementation
    // In a real app, you'd get this from the server
    setRemainingRequests(maxRequests - 1);
    
    return allowed;
  };

  return { isAllowed, remainingRequests, checkLimit };
};
